import { Link, ListItem, ListItemText } from '@material-ui/core';
import { Check } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import {
  Column,
  Columns,
  DrawerList,
  Stack,
  VisibilityObserver,
  useResponsiveValue,
  useSnackbarStack,
} from '@superdispatch/ui';
import { Box, Button, TextBox } from '@superdispatch/ui-lab';
import { partition } from 'es-toolkit/compat';
import { useMemo, useState } from 'react';
import {
  MutationDrawer,
  MutationDrawerContent,
} from 'shared/form/MutationDrawer';
import { trackEvent } from 'shared/helpers/Analytics';
import { LimitedExperienceDialog } from 'shared/modules/shipper/LimitedExperienceDialog';
import { useShipperListHasLimitedExperience } from 'shared/modules/shipper/ShipperUtils';
import { useFlag } from 'shared/settings/FeatureToggles';
import { emitLoadEvent } from '../../../carrier/loads/data/LoadEvents';
import {
  useAcceptOffers,
  useOffersList,
} from '../../../carrier/offers/offers/data/OffersAPI';
import { PermissionAction } from '../permission/core/PermissionAction';
import { PermissionIcon } from '../permission/core/PermissionIcon';
import { PermissionView } from '../permission/core/PermissionView';
import { LoadOffersDrawerItem } from './LoadOffersDrawerItem';
import { LoadOffersEmptyState } from './LoadOffersEmptyState';

const OffersTerms = () => (
  <TextBox color="secondary" display="inline">
    By accepting all offers you agree to our{' '}
    <Link
      target="_blank"
      rel="noopener noreferrer"
      href="https://www.superdispatch.com/terms-of-service"
    >
      Terms of Service
    </Link>
  </TextBox>
);

const OfferSkeleton = () => (
  <ListItem>
    <ListItemText
      disableTypography={true}
      primary={<Skeleton />}
      secondary={<Skeleton />}
    />
  </ListItem>
);

interface LoadOffersDrawerContentProps {
  onClose?: () => void;
}

export function LoadOffersDrawerContent({
  onClose,
}: LoadOffersDrawerContentProps) {
  const [dialog, setDialog] = useState<'limited-experience'>();
  const isNewShipperAlertEnabled = useFlag('new_shipper_alert');
  const offersList = useOffersList();
  const isMobile = useResponsiveValue(true, false);

  const { addSnackbar } = useSnackbarStack();
  const acceptOffers = useAcceptOffers({
    onSuccess({ user_message }, offers) {
      onClose?.();
      trackEvent('[CTMS] Accepted All Load Offers', {
        number_of_accepted_offers: offers.length,
      });
      addSnackbar(user_message || 'Offers accepted.', { variant: 'success' });
      emitLoadEvent('accepted');
    },
    onError({ message }) {
      addSnackbar(message || 'Failed to accept offers.', { variant: 'error' });
    },
  });

  const [counterOffers, offers] = useMemo(() => {
    const allOffers = offersList.data?.pages.flatMap((page) =>
      page.data.filter((offer) => {
        return !!offer.load;
      }),
    );

    return partition(allOffers, (offer) => offer.is_counter_offer);
  }, [offersList.data]);

  const { hasLimitedExperienceShipper, experienceList } =
    useShipperListHasLimitedExperience(
      isNewShipperAlertEnabled
        ? offers.map((offer) => offer.load?.broker_guid)
        : [],
    );

  const handleAcceptAllOffers = () => {
    if (hasLimitedExperienceShipper) {
      setDialog('limited-experience');
      return;
    }

    acceptOffers.mutate(offers.map((offer) => offer.guid));
  };

  return (
    <MutationDrawerContent
      onClose={onClose}
      title={
        <Columns space="small" align="center">
          <Column width={['fluid', 'content']}>
            <TextBox variant="heading-3">Load Offers</TextBox>
          </Column>
          <Column width="content">
            {!offersList.hasNextPage &&
              !!offersList.data?.pages[0]?.pagination.count &&
              !counterOffers.length && (
                <Box display="flex">
                  <PermissionAction name="marketplace" permission="can_manage">
                    <Button
                      variant="neutral"
                      startIcon={
                        <PermissionIcon
                          name="marketplace"
                          permission="can_manage"
                        >
                          <div>{!isMobile && <Check color="primary" />}</div>
                        </PermissionIcon>
                      }
                      size="small"
                      pending={acceptOffers.isPending}
                      onClick={handleAcceptAllOffers}
                    >
                      Accept All Offers
                    </Button>
                  </PermissionAction>
                </Box>
              )}
          </Column>
        </Columns>
      }
      enableAutoClose={true}
      disableDrawerContent={true}
    >
      {experienceList && (
        <LimitedExperienceDialog
          shippersExperience={experienceList}
          action="accept_offer"
          isOpen={dialog === 'limited-experience'}
          onCancel={() => {
            setDialog(undefined);
          }}
          onConfirm={() => {
            setDialog(undefined);
            acceptOffers.mutate(offers.map((offer) => offer.guid));
          }}
        />
      )}

      {!counterOffers.length && !!offers.length && (
        <Box
          maxWidth="480px"
          paddingLeft={['medium', 'large']}
          paddingRight={['medium', 'large']}
        >
          <OffersTerms />
        </Box>
      )}

      {!offersList.isPending &&
        !offersList.data?.pages[0]?.pagination.count && (
          <LoadOffersEmptyState />
        )}

      <DrawerList disablePadding={true}>
        {!!counterOffers.length && (
          <>
            <Box marginLeft="large">
              <TextBox variant="heading-4">Counter Offers</TextBox>
            </Box>

            {counterOffers.map((offer) => (
              <LoadOffersDrawerItem offer={offer} key={offer.guid} />
            ))}
          </>
        )}

        {!!counterOffers.length && !!offers.length && (
          <Box marginLeft="large" marginTop="medium">
            <Stack>
              <Columns align="center" space="small">
                <Column width={['fluid', 'content']}>
                  <TextBox variant="heading-4">Load Offers</TextBox>
                </Column>
                <Column width="content">
                  <Button
                    variant="neutral"
                    startIcon={!isMobile && <Check color="primary" />}
                    size="small"
                    pending={acceptOffers.isPending}
                    onClick={handleAcceptAllOffers}
                  >
                    Accept All Offers
                  </Button>
                </Column>
              </Columns>
              <OffersTerms />
            </Stack>
          </Box>
        )}

        {offers.map((offer) => (
          <LoadOffersDrawerItem offer={offer} key={offer.guid} />
        ))}

        {offersList.isPending && <OfferSkeleton />}

        {offersList.hasNextPage && (
          <VisibilityObserver
            key={offersList.data?.pages.length ?? 0}
            marginBottom="200px"
            render={({ ref }) => (
              <div ref={ref}>
                {offersList.isFetchingNextPage && <OfferSkeleton />}
              </div>
            )}
            onChange={(visibility) => {
              if (!offersList.isFetchingNextPage && visibility === 'visible') {
                void offersList.fetchNextPage();
              }
            }}
          />
        )}
      </DrawerList>
    </MutationDrawerContent>
  );
}

interface LoadOffersDrawerProps extends LoadOffersDrawerContentProps {
  open: boolean;
}

export function LoadOffersDrawer({ open, onClose }: LoadOffersDrawerProps) {
  return (
    <MutationDrawer open={open} onClose={onClose}>
      <PermissionView name="marketplace" permission="can_view">
        <LoadOffersDrawerContent onClose={onClose} />
      </PermissionView>
    </MutationDrawer>
  );
}
